import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, MapPin, Building2, Hash, User, Layers } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { searchDirectory } from '../../lib/api'
import type { DirectorySearchResult } from '../../types'

function ResultIcon({ type }: { type: DirectorySearchResult['type'] }) {
  const cls = 'h-4 w-4 flex-shrink-0'
  if (type === 'postcode') return <Hash className={`${cls} text-trust-500`} />
  if (type === 'area')     return <Layers className={`${cls} text-violet-500`} />
  if (type === 'town')     return <MapPin className={`${cls} text-emerald-500`} />
  if (type === 'county')   return <Building2 className={`${cls} text-amber-500`} />
  return <User className={`${cls} text-gray-400`} />
}

function typeLabel(type: DirectorySearchResult['type']) {
  if (type === 'postcode') return 'Postcode'
  if (type === 'area')     return 'Area'
  if (type === 'town')     return 'Town'
  if (type === 'county')   return 'County'
  return 'Plumber'
}

interface Props {
  placeholder?: string
  className?: string
}

export function DirectorySearchBar({ placeholder = 'Search by postcode, town or plumber…', className = '' }: Props) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounced(query, 280)

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['directory-search', debouncedQuery],
    queryFn: () => searchDirectory(debouncedQuery),
    enabled: debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
  })

  useEffect(() => {
    setActive(-1)
    setOpen(debouncedQuery.trim().length >= 2)
  }, [debouncedQuery])

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const go = useCallback((url: string) => {
    setOpen(false)
    setQuery('')
    navigate(url)
  }, [navigate])

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || !results.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(a => Math.min(a + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(a => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = active >= 0 ? results[active] : results[0]
      if (target) go(target.url)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (debouncedQuery.trim().length >= 2) setOpen(true) }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-trust-400 focus:border-transparent"
          autoComplete="off"
          spellCheck={false}
          aria-label="Search directory"
          aria-autocomplete="list"
          aria-expanded={open}
          role="combobox"
        />
        {isFetching && (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-trust-300 border-t-trust-600 animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-white rounded-xl border border-gray-100 shadow-xl overflow-hidden"
        >
          {results.map((r, i) => (
            <li
              key={`${r.type}-${r.url}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={e => { e.preventDefault(); go(r.url) }}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${i === active ? 'bg-trust-50' : 'hover:bg-gray-50'}`}
            >
              <ResultIcon type={r.type} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{r.label}</p>
                <p className="text-xs text-gray-400 truncate">{r.sublabel}</p>
              </div>
              <span className="text-xs text-gray-300 flex-shrink-0">{typeLabel(r.type)}</span>
            </li>
          ))}
        </ul>
      )}

      {open && !isFetching && results.length === 0 && debouncedQuery.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl border border-gray-100 shadow-xl px-4 py-3 text-sm text-gray-400">
          No results for "{debouncedQuery}"
        </div>
      )}
    </div>
  )
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}
